import { useMemo, useState } from "react";
import { Check, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateLabel,
    useDeleteLabel,
    useLabels,
    useUpdateLabel
} from "@/api/hooks";
import type { Label } from "@/api/mailApi";

const SWATCHES = [
    "#0078D4",
    "#107C10",
    "#F25022",
    "#FFB900",
    "#7FBA00",
    "#5C2D91",
    "#E81123",
    "#00B7C3"
];

export function LabelsPage() {
    const labels = useLabels();
    const create = useCreateLabel();
    const update = useUpdateLabel();
    const remove = useDeleteLabel();
    const [draft, setDraft] = useState({ name: "", color: SWATCHES[0]! });
    const [search, setSearch] = useState("");

    const allLabels = labels.data ?? [];
    const visibleLabels = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length === 0) return allLabels;
        return allLabels.filter((l) => l.name.toLowerCase().includes(q));
    }, [allLabels, search]);

    const submit = () => {
        if (!draft.name.trim()) return;
        create.mutate(
            { name: draft.name.trim(), color: draft.color, sortOrder: 0 },
            { onSuccess: () => setDraft({ name: "", color: SWATCHES[0]! }) }
        );
    };

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4">
                <h2 className="text-xl font-semibold">Labels</h2>
                <p className="text-sm text-muted-foreground">
                    Colour-coded tags for messages. Use the label picker in the reading pane
                    to apply them.
                </p>
            </header>

            <section className="mb-6 rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">New label</h3>
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Name
                        </label>
                        <Input
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            placeholder="Important"
                        />
                    </div>
                    <div>
                        <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Colour
                        </span>
                        <div className="flex gap-1">
                            {SWATCHES.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={`Pick ${c}`}
                                    onClick={() => setDraft({ ...draft, color: c })}
                                    className="flex h-7 w-7 items-center justify-center rounded-full border-2"
                                    style={{
                                        backgroundColor: c,
                                        borderColor: draft.color === c ? c : "transparent"
                                    }}
                                >
                                    {draft.color === c ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Button onClick={submit} disabled={create.isPending || !draft.name.trim()}>
                        {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Add
                    </Button>
                </div>
                {create.isError ? (
                    <p className="mt-2 text-xs text-destructive">{(create.error as Error).message}</p>
                ) : null}
            </section>

            {allLabels.length > 0 ? (
                <div className="relative mb-3 max-w-sm">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <Input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter by name"
                        aria-label="Filter labels"
                        className="h-8 pl-7 text-sm"
                    />
                </div>
            ) : null}

            {labels.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : allLabels.length === 0 ? (
                <p className="text-sm text-muted-foreground">No labels yet.</p>
            ) : visibleLabels.length === 0 ? (
                <p className="text-sm text-muted-foreground">No labels match &ldquo;{search}&rdquo;.</p>
            ) : (
                <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
                    <ul className="divide-y">
                        {visibleLabels.map((l) => (
                            <LabelRow
                                key={l.id}
                                label={l}
                                onRename={(name) =>
                                    update.mutate({
                                        id: l.id,
                                        payload: { name, color: l.color, sortOrder: l.sortOrder }
                                    })
                                }
                                onRecolor={(color) =>
                                    update.mutate({
                                        id: l.id,
                                        payload: { name: l.name, color, sortOrder: l.sortOrder }
                                    })
                                }
                                onDelete={() => {
                                    if (window.confirm(`Delete label "${l.name}"?`)) {
                                        remove.mutate(l.id);
                                    }
                                }}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function LabelRow({
    label,
    onRename,
    onRecolor,
    onDelete
}: {
    label: Label;
    onRename: (name: string) => void;
    onRecolor: (color: string) => void;
    onDelete: () => void;
}) {
    const [name, setName] = useState(label.name);
    return (
        <li className="flex items-center gap-3 px-4 py-3">
            <div className="flex gap-1">
                {SWATCHES.map((c) => (
                    <button
                        key={c}
                        type="button"
                        aria-label={`Recolour ${label.name} to ${c}`}
                        onClick={() => onRecolor(c)}
                        className="h-4 w-4 rounded-full border-2"
                        style={{
                            backgroundColor: c,
                            borderColor: label.color === c ? c : "transparent"
                        }}
                    />
                ))}
            </div>
            <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                    if (name.trim() && name !== label.name) onRename(name.trim());
                }}
                className="h-8 flex-1"
            />
            <Button variant="ghost" size="icon" onClick={onDelete} aria-label={`Delete ${label.name}`}>
                <Trash2 className="h-4 w-4" />
            </Button>
        </li>
    );
}
